import React, { useState, useMemo } from 'react';
import {
  Button,
  Container,
  Spinner,
} from 'react-bootstrap';
import { useNavigate, useParams, Link } from "react-router-dom";

import { getSpotMenu, updateMenu, deleteMenu } from 'src/Services/api';
import useQuery from 'src/Hooks/useQuery';
import useMutation from 'src/Hooks/useMutation';
import confirm from "src/Components/ConfirmDialog";
import JamlyfTable from "src/Components/Table";
import LoadingOverlay from "src/Components/LoadingOverlay";
import SearchBox from 'src/Components/SearchBox';
import Notification, { useNotification } from 'src/Components/Notification';
import { isSearchableString } from 'src/Utils/queryHelpers';

import './styles.scss';


const MENU_HEADER_TABLE_MAPPER = {
  name: "name",
  category: "category",
  is_available: "available"
};

const getMenuQueryParams = (spotId, searchTerm, pageNumber) => {
  const queryParams = { spot: spotId }
  if (!!searchTerm && isSearchableString(searchTerm)) {
    queryParams["search"] = searchTerm;
  }
  if (!!pageNumber) {
    queryParams["page"] = pageNumber;
  }
  return queryParams;
}

const MenuListPage = () => {
  const [searchStr, setSearchStr] = useState("");
  const [pageNum, setPageNum] = useState(1);
  const { id: spotId } = useParams();
  const { notifySuccess, notifyError, onClose, notification } = useNotification();

  const queryParams = getMenuQueryParams(spotId, searchStr, pageNum)
  const { loading, response = {}, refetch: refetchSpotMenu } = useQuery(getSpotMenu, queryParams)
  const { data = {} } = response;
  const { objects: menuList = [], total_pages } = data;

  const availableMenuSet = useMemo(() => {
    if (menuList) {
      const availableMenu = menuList.filter((menuItem) => menuItem.is_available)
      return new Set(availableMenu.map((menuItem) => menuItem.id))
    }
  }, [menuList])

  const { setter: deleteMenuAction, loading: deleteMenuLoading } = useMutation(deleteMenu)
  const { setter: updateMenuAction, loading: updateMenuLoading } = useMutation(updateMenu)
  const navigate = useNavigate();

  const handleDeleteMenu = async (menu) => {
    const menuId = menu.id;
    if (await confirm(`Are you sure you want to delete this menu?`)) {
      deleteMenuAction(
        { id: menuId },
        () => {
          notifySuccess({ message: "Menu deleted successfully" });
          refetchSpotMenu();
        },
        () => notifyError({ message: "Menu delete failed" }),
      );
    }
  }

  const handleEditButtonClick = (menu) => {
    navigate(`${menu.id}`);
  };

  const handleUpdateMenu = (menu) => async (event) => {
    const menuId = menu.id;
    event.preventDefault();
    const newMenuData = new FormData();
    newMenuData.append("is_available", !availableMenuSet.has(menuId));
    updateMenuAction(
      { id: menuId, data: newMenuData },
      () => {
        notifySuccess({ message: "Menu updated successfully" });
        refetchSpotMenu();
      },
      () => notifyError({ message: "Error: Menu update failed" }),
    );
  }

  if (loading && !menuList) {
    return (
      <Container className="d-flex justify-content-center pt-5">
        <Spinner animation="border">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    )
  }

  const hasMenu = !!menuList?.length
  const isLoadingMore = loading && hasMenu

  return (
    <Container className="mt-4">
      <div style={{ paddingBottom: 20, paddingTop: 20 }}>
        <div className="d-inline-flex align-items-center">
          <h5>Food Menu</h5>
        </div>
        <Link to="new" className="text-white text-decoration-none">
          <Button className='jamlyfSolidBtn' style={{ float: 'right' }}>
            Add menu
          </Button>
        </Link>
      </div>
      <div className="d-inline-block col-12 col-lg-8 mb-4">
        <SearchBox
          id="formMenuSearch"
          placeholder="Search menu"
          defaultValue={searchStr}
          onChange={setSearchStr}
          isLoading={loading && isSearchableString(searchStr)}
          onReset={() => setSearchStr("")}
          disabled={!searchStr && !hasMenu}
        />
      </div>
      <JamlyfTable
        data={menuList}
        onEditActionClick={handleEditButtonClick}
        onDeleteActionClick={handleDeleteMenu}
        handleBoolUpdate={handleUpdateMenu}
        headerMapper={MENU_HEADER_TABLE_MAPPER}
        headers={Object.values(MENU_HEADER_TABLE_MAPPER)}
        onPageChange={setPageNum}
        totalPages={total_pages}
        showActions
      />
      <Notification id="menu-list-notif" onClose={onClose} {...notification} />
      <LoadingOverlay show={deleteMenuLoading || updateMenuLoading || isLoadingMore} />
    </Container>
  );
};


export default MenuListPage;
